import type { GmbAuditHistoryItem } from '@/pages/gmb-audit/data/auditApi';

// UI-2C｜健康度趨勢 Line Chart（純 SVG，無動畫、無外部 library、無重新 API request）。
// 只讀已保存的 score snapshot；不重算分數、不呼叫 Google / Peer / Scoring。

export interface HistoryTrendPoint {
  token: string;
  date: string;
  score: number;
}

/** 由歷史摘要產生趨勢點（只取有分數者，依日期升冪）。 */
export function buildTrendPoints(records: GmbAuditHistoryItem[]): HistoryTrendPoint[] {
  return records
    .filter((r) => typeof r.score === 'number')
    .map((r) => ({ token: r.reportToken, date: r.createdAt, score: r.score as number }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}

/**
 * 簡單折線圖。value 標籤置於點上方、日期標籤置於下方；同時提供可讀的數字 chips。
 * 手機：圖表容器有最大寬度，避免文字被過度縮小；數字 chips 以 flex-wrap 呈現。
 */
export default function HistoryTrendChart({ points }: { points: HistoryTrendPoint[] }) {
  if (points.length < 2) return null;

  const n = points.length;
  const W = 480;
  const H = 220;
  const padL = 38;
  const padR = 18;
  const padT = 30;
  const padB = 46;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const scores = points.map((p) => p.score);
  const rawMin = Math.min(...scores);
  const rawMax = Math.max(...scores);
  let yMin = Math.max(0, Math.floor((rawMin - 4) / 10) * 10);
  let yMax = Math.min(100, Math.ceil((rawMax + 4) / 10) * 10);
  if (yMax - yMin < 10) yMax = Math.min(100, yMin + 10);
  if (yMax === yMin) yMax = yMin + 10;
  const span = yMax - yMin;

  const xAt = (i: number) => padL + (plotW * i) / (n - 1);
  const yAt = (v: number) => padT + plotH * (1 - (v - yMin) / span);

  const linePoints = points.map((p, i) => `${xAt(i)},${yAt(p.score)}`).join(' ');
  const areaPoints = `${padL},${padT + plotH} ${linePoints} ${xAt(n - 1)},${padT + plotH}`;
  const midValue = yMin + span / 2;

  const summaryLabel = `健康度趨勢折線圖，共 ${n} 筆：${points
    .map((p) => `${formatShortDate(p.date)} ${p.score} 分`)
    .join('、')}`;

  return (
    <div className="mt-4 rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center gap-2">
        <i className="ri-line-chart-line text-lg text-primary-700" />
        <h3 className="text-base font-bold text-foreground-950 md:text-lg">健康度趨勢</h3>
      </div>
      <p className="mt-1 text-sm text-foreground-500">只依已保存的分數紀錄繪製，不會重新計算歷史分數。</p>

      <div className="mx-auto mt-3 w-full max-w-[560px]">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={summaryLabel}
          className="h-auto w-full"
        >
          {/* 網格線 */}
          {[yMax, midValue, yMin].map((v) => (
            <line
              key={v}
              x1={padL}
              y1={yAt(v)}
              x2={W - padR}
              y2={yAt(v)}
              style={{ stroke: 'oklch(var(--background-300))', strokeWidth: 1 }}
            />
          ))}
          {/* Y 軸刻度 */}
          <text x={padL - 8} y={yAt(yMax) + 4} textAnchor="end" fontSize={12} style={{ fill: 'oklch(var(--foreground-500))' }}>
            {Math.round(yMax)}
          </text>
          <text x={padL - 8} y={yAt(yMin) + 4} textAnchor="end" fontSize={12} style={{ fill: 'oklch(var(--foreground-500))' }}>
            {Math.round(yMin)}
          </text>

          {/* 面積 + 折線 */}
          <polygon points={areaPoints} style={{ fill: 'oklch(var(--primary-100))', opacity: 0.5 }} />
          <polyline
            points={linePoints}
            fill="none"
            style={{ stroke: 'oklch(var(--primary-600))', strokeWidth: 2.5, strokeLinejoin: 'round', strokeLinecap: 'round' }}
          />

          {/* 資料點 + 數值 + 日期 */}
          {points.map((p, i) => (
            <g key={p.token}>
              <circle
                cx={xAt(i)}
                cy={yAt(p.score)}
                r={4}
                style={{ fill: 'oklch(var(--primary-500))', stroke: 'oklch(var(--background-50))', strokeWidth: 2 }}
              />
              <text x={xAt(i)} y={yAt(p.score) - 12} textAnchor="middle" fontSize={14} fontWeight={700} style={{ fill: 'oklch(var(--foreground-900))' }}>
                {p.score}
              </text>
              <text x={xAt(i)} y={H - padB + 22} textAnchor="middle" fontSize={12} style={{ fill: 'oklch(var(--foreground-500))' }}>
                {formatShortDate(p.date)}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* 數字 chips（同時提供可讀數字，不依賴圖表） */}
      <div className="mt-3 flex flex-wrap gap-2">
        {points.map((p) => (
          <span key={p.token} className="ds-badge bg-secondary-100 text-secondary-900">
            {formatShortDate(p.date)}・{p.score} 分
          </span>
        ))}
      </div>
    </div>
  );
}